import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Lock, Mail, ArrowLeft } from 'lucide-react'
import { LANGUAGES } from '@olimhub/shared'
import { authApi } from '@/api'
import { getAuthError } from '@/lib/auth-error'
import { useAuthStore } from '@/store/auth.store'
import { useUiStore } from '@/store/ui.store'
import logoLight from '@/assets/logo/olimhub-logo.png'
import logoDark from '@/assets/logo/olimhub-logo-dark.png'

const UI_LANGS = LANGUAGES.filter((l) => l !== 'he')

function LangSelector() {
  const { i18n } = useTranslation()
  const currentLang = i18n.language.slice(0, 2)

  return (
    <div
      role="group"
      aria-label="Language"
      style={{
        display: 'inline-flex',
        padding: 3,
        gap: 2,
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-pill)',
      }}
    >
      {UI_LANGS.map((l) => {
        const on = currentLang === l
        return (
          <button
            key={l}
            type="button"
            onClick={() => {
              void i18n.changeLanguage(l)
              localStorage.setItem('i18nextLng', l)
              document.documentElement.lang = l
              document.documentElement.dir = 'ltr'
            }}
            aria-pressed={on}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '5px 12px',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              background: on ? 'var(--brand)' : 'transparent',
              color: on ? 'var(--on-brand)' : 'var(--text-muted)',
              transition: 'background var(--dur-fast) var(--ease-out)',
            }}
          >
            {l.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}

function PasswordField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  const [show, setShow] = useState(false)
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-strong)',
        }}
      >
        {label}
      </span>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <span
          style={{
            position: 'absolute',
            insetInlineStart: 12,
            color: 'var(--text-muted)',
            display: 'flex',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Lock size={18} />
        </span>
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          style={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            padding: '11px 44px 11px 40px',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            background: 'var(--surface)',
            color: 'var(--text-strong)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((v) => !v)}
          style={{
            position: 'absolute',
            insetInlineEnd: 0,
            width: 44,
            height: '100%',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            borderRadius: '0 var(--radius-md) var(--radius-md) 0',
          }}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  padding: '11px 12px 11px 40px',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  background: 'var(--surface)',
  color: 'var(--text-strong)',
  outline: 'none',
  boxSizing: 'border-box',
}

export function Login() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, setAuth } = useAuthStore()
  const { theme } = useUiStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Read ?from= query param for post-login redirect
  const searchParams = new URLSearchParams(location.search)
  const rawFrom = searchParams.get('from')
  const from = rawFrom ? decodeURIComponent(rawFrom) : '/'

  // Already authenticated → go straight to the original destination (e.g. a
  // stale/bookmarked ?from= login link opened while already signed in).
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, from])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await authApi.signin({ email, password })
      setAuth(result.accessToken, result.account)
      const loginCount = parseInt(localStorage.getItem('loginCount') ?? '0', 10)
      localStorage.setItem('loginCount', String(loginCount + 1))
      navigate(from, { replace: true })
    } catch (err: unknown) {
      setError(getAuthError(err, t, 'login'))
    } finally {
      setLoading(false)
    }
  }

  const logo = theme === 'dark' ? logoDark : logoLight
  const dir = i18n.dir()

  return (
    <div
      dir={dir}
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
      }}
    >
      {/* Scrollable card area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 20px 32px',
          overflowY: 'auto',
        }}
      >
        {/* Card */}
        <div
          style={{
            width: '100%',
            maxWidth: 'var(--feed-max)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
            padding: '28px 20px 24px',
            boxSizing: 'border-box',
          }}
        >
          {/* Logo + spectrum + lang selector */}
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}
          >
            <Link to="/">
              <img src={logo} alt="OlimHub" style={{ height: 34, width: 'auto', display: 'block' }} />
            </Link>
            <div className="oh-spectrum oh-spectrum--smooth" style={{ width: 52, height: 4 }} />
            <LangSelector />
          </div>

          {/* Heading */}
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <h1
              style={{
                fontSize: 'var(--fs-h2)',
                fontWeight: 700,
                color: 'var(--text-strong)',
                letterSpacing: '-0.02em',
                margin: 0,
              }}
            >
              {t('auth.login_title')}
            </h1>
            <p
              style={{
                margin: '8px auto 0',
                maxWidth: '30ch',
                color: 'var(--text-muted)',
                fontSize: 'var(--fs-sm)',
                lineHeight: 'var(--lh-normal)',
              }}
            >
              {t('auth.login_sub')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={(e) => { void handleSubmit(e) }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Email */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-strong)',
                }}
              >
                {t('auth.email')}
              </span>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <span
                  style={{
                    position: 'absolute',
                    insetInlineStart: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <Mail size={18} />
                </span>
                <input
                  type="email"
                  placeholder={t('auth.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
            </label>

            <PasswordField
              label={t('auth.password')}
              placeholder={t('auth.password_placeholder')}
              value={password}
              onChange={setPassword}
            />

            {/* Forgot password */}
            <div style={{ marginTop: -4, textAlign: 'end' }}>
              <button
                type="button"
                onClick={() => navigate('/password-recovery')}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--brand)',
                }}
              >
                {t('auth.forgot_password')}
              </button>
            </div>

            {error && (
              <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--danger)', textAlign: 'center' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4,
                padding: '13px 0',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--brand)',
                color: 'var(--on-brand)',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 'var(--fs-body)',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                width: '100%',
              }}
            >
              {loading ? t('common.loading') : t('auth.login_cta')}
            </button>
          </form>

          <div style={{ height: 1, background: 'var(--border)', margin: '20px 0 16px' }} />

          <div style={{ textAlign: 'center', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            {t('auth.to_signup')}{' '}
            <button
              type="button"
              onClick={() => navigate(rawFrom ? `/signup?from=${rawFrom}` : '/signup')}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 'var(--fs-sm)',
                color: 'var(--brand)',
              }}
            >
              {t('auth.to_signup_cta')}
            </button>
          </div>
        </div>

        {/* Back to home */}
        <Link
          to="/"
          style={{
            marginTop: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={16} />
          Back to home
        </Link>
      </div>
    </div>
  )
}
